/*
 * predictive-search.js — WAI-ARIA-Combobox-Island für <predictive-search>.
 * docs/02 D1/D2 · docs/03 §JS-Architektur · docs/06 §Geteilte Chrome.
 *
 * Aufgaben:
 *  - ≥ min-chars (Default 2), Debounce (Default 150ms), AbortController gegen Race-Conditions
 *  - fetch {{ routes.predictive_search_url }}?q=…&resources[type]=product,collection,page,article
 *      &resources[limit]=…&section_id=predictive-search  → HTML in [data-predictive-results]
 *  - aria-expanded / aria-activedescendant pflegen, Optionen role=option (aus dem Fragment)
 *  - Tastatur: ArrowDown/Up (roving), Enter (Option folgen ODER Suche absenden), Escape, Home/End
 *  - Desktop: Klick-außerhalb schließt das Dropdown; Mobile: Fullscreen-Overlay
 *      via [data-search-open] (global) + overlay.js (Body-Scroll-Lock/Escape/Fokus-Restore)
 *  - role=status-Trefferzahl (im Fragment als [data-ps-count]) → an Live-Region weitergeben
 *
 * Progressive Enhancement: ohne JS bleibt das <form> ein nativer GET-Suchlauf.
 * Nutzt Shopify.routes.root für locale-aware URLs; fällt auf '/' zurück.
 */
(function () {
  'use strict';

  if (window.customElements && window.customElements.get('predictive-search')) return;

  var MOBILE_MQ = '(max-width: 900px)';

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }
  function predictiveUrl() {
    return root().replace(/\/$/, '') + '/search/suggest';
  }
  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }
  function isMobile() {
    return window.matchMedia && window.matchMedia(MOBILE_MQ).matches;
  }
  function overlay() {
    return window.Homedia && window.Homedia.overlay;
  }

  var PredictiveSearch;

  // Als echtes Custom-Element registrieren (mit Fallback auf direktes Wiring).
  if (window.customElements && window.HTMLElement) {
    PredictiveSearch = class extends HTMLElement {
      connectedCallback() { init(this); }
      disconnectedCallback() { teardown(this); }
    };
    window.customElements.define('predictive-search', PredictiveSearch);
  } else {
    // Sehr alte Browser: einmalig manuell initialisieren.
    document.addEventListener('DOMContentLoaded', function () {
      var nodes = document.querySelectorAll('predictive-search[data-predictive]');
      Array.prototype.forEach.call(nodes, init);
    });
  }

  // -------------------------------------------------------------------------
  function init(el) {
    if (el.__psInit) return;
    el.__psInit = true;

    var state = {
      input: el.querySelector('[data-ps-input]'),
      results: el.querySelector('[data-predictive-results]'),
      empty: el.querySelector('[data-ps-empty]'),
      clear: el.querySelector('[data-ps-clear]'),
      closeBtn: el.querySelector('[data-ps-close]'),
      sectionId: el.getAttribute('data-section-id') || 'predictive-search',
      minChars: parseInt(el.getAttribute('data-min-chars'), 10) || 2,
      limit: parseInt(el.getAttribute('data-limit'), 10) || 6,
      controller: null,
      active: -1,        // Index der aktiven Option (aria-activedescendant)
      options: [],       // aktuelle [data-ps-option]-Nodes
      open: false,
      mobileOpen: false
    };
    el.__psState = state;
    if (!state.input || !state.results) return;

    var run = debounce(function () { fetchResults(el, state); }, parseInt(el.getAttribute('data-debounce'), 10) || 150);

    // ---- Eingabe ----
    state.input.addEventListener('input', function () {
      toggleClear(state);
      var q = state.input.value.trim();
      if (q.length < state.minChars) {
        abort(state);
        showEmpty(el, state);
        return;
      }
      run();
    });

    // Fokus öffnet (Desktop-Dropdown) — zeigt Empty oder letzte Treffer.
    state.input.addEventListener('focus', function () {
      if (!isMobile()) openDropdown(el, state);
    });

    state.input.addEventListener('keydown', function (e) { onKeydown(el, state, e); });

    // ---- Clear ----
    if (state.clear) {
      state.clear.addEventListener('click', function () {
        state.input.value = '';
        toggleClear(state);
        abort(state);
        showEmpty(el, state);
        state.input.focus();
      });
    }

    // ---- Schließen (Mobile-Zurück / data-overlay-close-artig) ----
    if (state.closeBtn) {
      state.closeBtn.addEventListener('click', function () { closeMobile(el, state); });
    }

    // ---- Hover über einer Option pflegt die aktive Selektion (Klick folgt dem nativen <a>) ----
    el.addEventListener('mousemove', function (e) {
      var opt = e.target.closest('[data-ps-option]');
      if (opt) setActiveByNode(state, opt);
    });

    // ---- Klick außerhalb (Desktop) schließt Dropdown ----
    el.__psDocClick = function (e) {
      if (isMobile()) return;
      if (!el.contains(e.target)) closeDropdown(el, state);
    };
    document.addEventListener('click', el.__psDocClick);

    toggleClear(state);
  }

  function teardown(el) {
    if (el.__psDocClick) document.removeEventListener('click', el.__psDocClick);
    abort(el.__psState || {});
  }

  // -------------------------------------------------------------------------
  function abort(state) {
    if (state.controller) { try { state.controller.abort(); } catch (e) {} state.controller = null; }
  }

  function toggleClear(state) {
    if (!state.clear) return;
    state.clear.hidden = !state.input.value;
  }

  function fetchResults(el, state) {
    var q = state.input.value.trim();
    if (q.length < state.minChars) { showEmpty(el, state); return; }

    abort(state);
    var controller = ('AbortController' in window) ? new AbortController() : null;
    state.controller = controller;

    var params = new URLSearchParams();
    params.set('q', q);
    params.set('resources[type]', 'product,collection,page,article');
    params.set('resources[limit]', String(state.limit));
    params.set('resources[options][unavailable_products]', 'last');
    params.set('section_id', state.sectionId);

    fetch(predictiveUrl() + '?' + params.toString(), {
      signal: controller ? controller.signal : undefined,
      headers: { 'Accept': 'text/html' }
    })
      .then(function (r) {
        if (!r.ok) throw new Error('search ' + r.status);
        return r.text();
      })
      .then(function (html) {
        injectResults(el, state, html);
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        // Bei Fehler: Empty-State zeigen statt kaputter UI.
        showEmpty(el, state);
      });
  }

  function injectResults(el, state, html) {
    // Das Fragment ist eine ganze Section; wir extrahieren nur den inneren Inhalt.
    var tmp = document.createElement('div');
    tmp.innerHTML = html.trim();
    // Shopify wrappt Section-Output in <div id="shopify-section-…"> — flach auspacken.
    var wrapper = tmp.querySelector('.shopify-section') || tmp;

    state.results.innerHTML = wrapper.innerHTML;
    state.results.hidden = false;
    if (state.empty) state.empty.hidden = true;

    refreshOptions(state);
    state.active = -1;
    setExpanded(state, true);

    // role=status: Trefferzahl ansagen (aus dem Fragment) — über die geteilte
    // Live-Region von cart.js (Homedia.cart.announce, Guard falls cart.js je fehlt).
    var count = state.results.querySelector('[data-ps-count]');
    if (count && window.Homedia && Homedia.cart && Homedia.cart.announce) Homedia.cart.announce(count.textContent.trim());
  }

  function showEmpty(el, state) {
    state.results.hidden = true;
    state.results.innerHTML = '';
    if (state.empty) state.empty.hidden = false;
    state.options = [];
    state.active = -1;
    clearActiveDescendant(state);
    // Dropdown bleibt offen (Empty-State sichtbar), Combobox-Status = collapsed (keine Optionen).
    state.input.setAttribute('aria-expanded', String(!!state.open || !!state.mobileOpen));
  }

  function refreshOptions(state) {
    state.options = Array.prototype.slice.call(state.results.querySelectorAll('[data-ps-option]'));
  }

  // ---- Combobox-Expansion / aria-activedescendant -------------------------
  function setExpanded(state, expanded) {
    state.input.setAttribute('aria-expanded', String(expanded));
  }
  function clearActiveDescendant(state) {
    state.input.removeAttribute('aria-activedescendant');
    state.options.forEach(function (o) { o.setAttribute('aria-selected', 'false'); });
  }
  function setActive(state, idx) {
    if (!state.options.length) return;
    if (idx < 0) idx = state.options.length - 1;
    if (idx >= state.options.length) idx = 0;
    state.options.forEach(function (o, i) { o.setAttribute('aria-selected', i === idx ? 'true' : 'false'); });
    var node = state.options[idx];
    state.active = idx;
    if (node.id) state.input.setAttribute('aria-activedescendant', node.id);
    node.scrollIntoView({ block: 'nearest' });
  }
  function setActiveByNode(state, node) {
    var idx = state.options.indexOf(node);
    if (idx > -1) setActive(state, idx);
  }

  // ---- Tastatur -----------------------------------------------------------
  function onKeydown(el, state, e) {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!state.options.length) { return; }
        setActive(state, state.active + 1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (!state.options.length) { return; }
        setActive(state, state.active - 1);
        break;
      case 'Home':
        if (state.options.length) { e.preventDefault(); setActive(state, 0); }
        break;
      case 'End':
        if (state.options.length) { e.preventDefault(); setActive(state, state.options.length - 1); }
        break;
      case 'Enter':
        if (state.active > -1 && state.options[state.active]) {
          // Aktive Option folgen (echtes <a>).
          e.preventDefault();
          var node = state.options[state.active];
          if (node.href) { window.location.assign(node.href); }
          else { node.click(); }
        }
        // sonst: nativer Form-Submit (Volltext-Suche) — nicht abfangen.
        break;
      case 'Escape':
        if (isMobile() && state.mobileOpen) {
          e.preventDefault();
          closeMobile(el, state);
        } else if (state.input.value) {
          e.preventDefault();
          state.input.value = '';
          toggleClear(state);
          abort(state);
          showEmpty(el, state);
        } else {
          closeDropdown(el, state);
        }
        break;
    }
  }

  // ---- Desktop-Dropdown ---------------------------------------------------
  function openDropdown(el, state) {
    state.open = true;
    el.setAttribute('data-open', '');
    // Hat der Nutzer schon getippt? Dann Treffer behalten, sonst Empty.
    if (state.input.value.trim().length >= state.minChars && state.options.length) {
      setExpanded(state, true);
    } else if (state.input.value.trim().length >= state.minChars) {
      fetchResults(el, state);
    } else {
      showEmpty(el, state);
    }
  }
  function closeDropdown(el, state) {
    state.open = false;
    el.removeAttribute('data-open');
    setExpanded(state, false);
    clearActiveDescendant(state);
    state.active = -1;
  }

  // ---- Mobile-Fullscreen-Overlay -----------------------------------------
  function openMobile(el, state, trigger) {
    if (state.mobileOpen) return;
    state.mobileOpen = true;
    el.setAttribute('data-mobile-open', '');
    showEmpty(el, state);

    var ov = overlay();
    if (ov) {
      // overlay.js liefert Body-Scroll-Lock, Escape, Fokus-Restore, Fokus-Trap.
      ov.open(el, {
        trigger: trigger,
        labelledby: null,
        onClose: function () {
          state.mobileOpen = false;
          el.removeAttribute('data-mobile-open');
        }
      });
    } else {
      document.body.classList.add('hm-scroll-locked');
    }
    setTimeout(function () { try { state.input.focus(); } catch (e) {} }, 60);
  }
  function closeMobile(el, state) {
    if (!state.mobileOpen) {
      // Desktop-Pfad: Zurück-Button auch dort tolerieren.
      closeDropdown(el, state);
      return;
    }
    var ov = overlay();
    if (ov && ov.isOpen(el)) {
      ov.close(el); // onClose räumt data-mobile-open auf
    } else {
      state.mobileOpen = false;
      el.removeAttribute('data-mobile-open');
      document.body.classList.remove('hm-scroll-locked');
    }
  }

  // ---- Globaler Trigger: [data-search-open] (aus Header/Bottom-Nav) -------
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-search-open]');
    if (!trigger) return;
    var el = document.querySelector('predictive-search[data-predictive]');
    if (!el) return;
    e.preventDefault();
    var state = el.__psState;
    if (!state) { return; }
    if (isMobile()) {
      openMobile(el, state, trigger);
    } else {
      state.input.focus();
      openDropdown(el, state);
    }
  });

})();
